import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, formatDistanceToNow } from "date-fns";
import { Bell, Check, CheckCheck, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getSeverityConfig } from "@/components/common/NotificationsMenu";
import { useWebSocket } from "@/contexts/WebSocketContext";
import {
  getNotificationsPage,
  MarkAsReadNotificationbyId,
  MarkAsALLReadNotifications,
  deleteNotificationById,
  deleteAllNotification,
} from "@/services/employeeService";
import { announceNotificationsChanged, onNotificationsChanged } from "@/lib/notifications";
import { getErrorMessage } from "@/utils/errorHandler";

const PAGE_SIZE = 20;
const FILTERS = [
  { value: "all", label: "All" },
  { value: "unread", label: "Unread" },
  { value: "read", label: "Read" },
];

/**
 * Full notifications inbox, shared by admins, employees and super admins.
 * `resolveRoute(notification)` → { path, state? } | null decides where a click
 * goes (see lib/notifications.js), same as the bell dropdown.
 */
export default function NotificationsPage({ resolveRoute }) {
  const navigate = useNavigate();
  const { addNotificationListener } = useWebSocket();

  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, unread: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmClearAll, setConfirmClearAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getNotificationsPage({ page, limit: PAGE_SIZE, status: filter });
      setData({ items: res?.items || [], total: res?.total || 0, unread: res?.unread || 0 });
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load notifications"));
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Stay current: new notifications arrive over the socket, and the bell can
  // change read/cleared state while this page is open.
  useEffect(() => {
    const unsubscribeSocket = addNotificationListener(() => load());
    const unsubscribeChanges = onNotificationsChanged(() => load());
    return () => {
      unsubscribeSocket();
      unsubscribeChanges();
    };
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  // A delete can empty the last page — step back instead of showing nothing.
  useEffect(() => {
    if (!loading && page > totalPages) setPage(totalPages);
  }, [loading, page, totalPages]);

  const changeFilter = (value) => {
    setFilter(value);
    setPage(1);
  };

  const afterChange = async () => {
    announceNotificationsChanged();
    await load();
  };

  const markRead = async (notification) => {
    setBusyId(notification._id);
    try {
      await MarkAsReadNotificationbyId(notification._id, true);
      await afterChange();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to mark as read"));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (notification) => {
    setBusyId(notification._id);
    try {
      await deleteNotificationById(notification._id);
      toast.success("Notification deleted");
      await afterChange();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete notification"));
    } finally {
      setBusyId(null);
    }
  };

  const markAllRead = async () => {
    try {
      await MarkAsALLReadNotifications();
      toast.success("All notifications marked as read");
      await afterChange();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to mark notifications as read"));
    }
  };

  const clearAll = async () => {
    try {
      await deleteAllNotification();
      toast.success("All notifications deleted");
      setPage(1);
      await afterChange();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete notifications"));
    } finally {
      setConfirmClearAll(false);
    }
  };

  const open = async (notification) => {
    if (!notification.is_read) {
      try {
        await MarkAsReadNotificationbyId(notification._id, true);
        announceNotificationsChanged();
      } catch {
        // Navigation still matters more than the read flag.
      }
    }
    const route = resolveRoute?.(notification);
    if (route?.path) {
      navigate(route.path, route.state ? { state: route.state } : undefined);
    } else {
      load();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Notifications</h1>
          <p className="mt-1 text-muted-foreground">
            {data.unread > 0
              ? `You have ${data.unread} unread notification${data.unread === 1 ? "" : "s"}.`
              : "You're all caught up."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={markAllRead} disabled={data.unread === 0}>
            <CheckCheck className="mr-2 h-4 w-4" />
            Mark all as read
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => setConfirmClearAll(true)}
            disabled={data.total === 0 && filter === "all"}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete all
          </Button>
        </div>
      </div>

      {/* Filter */}
      <div className="inline-flex rounded-lg border border-border bg-muted/40 p-1">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => changeFilter(f.value)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              filter === f.value
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {f.label}
            {f.value === "unread" && data.unread > 0 && (
              <span className="ml-1.5 rounded-full bg-primary/15 px-1.5 text-xs text-primary">
                {data.unread}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="divide-y divide-border">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex gap-4 p-4">
                  <Skeleton className="h-9 w-9 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-3 w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : data.items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                <Bell className="h-7 w-7 text-muted-foreground" />
              </div>
              <p className="font-semibold text-foreground">
                {filter === "unread"
                  ? "No unread notifications"
                  : filter === "read"
                    ? "No read notifications"
                    : "No notifications yet"}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {data.items.map((n) => {
                const { icon, color } = getSeverityConfig(n.type);
                const created = n.createdAt ? new Date(n.createdAt) : null;
                const validDate = created && !Number.isNaN(created.getTime());

                return (
                  <li
                    key={n._id}
                    className={`group relative flex gap-4 p-4 transition-colors ${
                      n.is_read ? "hover:bg-muted/40" : "bg-primary/5 hover:bg-primary/10"
                    }`}
                  >
                    {!n.is_read && (
                      <span className="absolute bottom-0 left-0 top-0 w-[3px] rounded-r-full bg-primary" />
                    )}

                    <button
                      type="button"
                      onClick={() => open(n)}
                      className="flex min-w-0 flex-1 gap-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-md"
                    >
                      <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${color}`}>
                        {icon}
                      </span>
                      <span className="min-w-0 flex-1 space-y-1">
                        <span
                          className={`block text-sm ${
                            n.is_read ? "font-medium text-muted-foreground" : "font-semibold text-foreground"
                          }`}
                        >
                          {n.title}
                        </span>
                        {n.message && (
                          <span className="block text-sm text-muted-foreground">{n.message}</span>
                        )}
                        {validDate && (
                          <span
                            className="block text-xs text-muted-foreground"
                            title={format(created, "dd MMM yyyy, p")}
                          >
                            {formatDistanceToNow(created, { addSuffix: true })}
                          </span>
                        )}
                      </span>
                    </button>

                    <div className="flex shrink-0 items-start gap-1">
                      {!n.is_read && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          title="Mark as read"
                          aria-label="Mark as read"
                          disabled={busyId === n._id}
                          onClick={() => markRead(n)}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        title="Delete"
                        aria-label="Delete"
                        disabled={busyId === n._id}
                        onClick={() => remove(n)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {!loading && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages} · {data.total} notifications
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="mr-1 h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={confirmClearAll} onOpenChange={setConfirmClearAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete all notifications?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes every notification from your list. It doesn't affect anyone
              else's notifications and can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={clearAll}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
